/* eslint-disable react/jsx-key */
/*eslint-disable eqeqeq*/
import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from "react";
import GoogleMapReact from "google-map-react";
import RoomIcon from "@mui/icons-material/Room";
import SchoolIcon from "@mui/icons-material/School";
import { useTranslation } from "react-i18next";
import DirectionsBus from "@mui/icons-material/DirectionsBus";
import CheckCircle from "@mui/icons-material/CheckCircle";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import PersonAdd from "@mui/icons-material/PersonAdd";

import { useForm } from "shared/hooks/form-hook";

// #region Components
import ErrorModal from "shared/components/UIElements/ErrorModal";
import Marker from "shared/components/UIElements/Marker";
import Player from "shared/components/UIElements/player";
import { PlayerStates } from "shared/util/types";
import { isPickUp } from "shared/util/util";
import CustomSelect from "shared/components/FormElements/Select";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
// import Skeleton from "@mui/material/Skeleton";

import {
  TRAVEL_ACTION_TYPE,
  TRAVEL_ACTION_TYPE_EXPLANATIONS,
  APPROACH_TO_HOME_TYPE_EXPLANATIONS,
  STUDENT_GETON_INFO_TYPE_EXPLANATIONS,
  ARRIVED_SCHOOL_TYPE_EXPLANATIONS,
  ARRIVED_SCHOOL_TYPE,
  LEAVED_SCHOOL_TYPE,
  TRAVEL_LEAVED_TYPE_EXPLANATIONS,
  TRAVEL_STATUS_TYPE,
  TRAVEL_STATUS_TYPE_EXPLANATIONS,
} from "shared/util/types";
import Button from "shared/components/FormElements/Button";
import { PERMISSION_TYPES, COMPANY_DOMAINS } from "@routebot/constants";
import { findVehicleByPlate } from "shared/util/normalize-plate";
import { formatIstanbulWallClock, getTodayInTimezone } from "shared/utility/date";
import RideSkipLivePanel from "features/ride-skip/RideSkipLivePanel";
import { RIDE_SKIP_SERVICE_KINDS } from "features/ride-skip/constants";
import useTelemetryVehicle from "features/telemetry/hooks/useTelemetryVehicle";
import { useTelemetryByPlate } from "features/telemetry/hooks/useTelemetryLive";
import VehicleTelemetryMarker from "features/telemetry/VehicleTelemetryMarker";
import { formatTelemetryAge } from "features/telemetry/util/format-age";
// #endregion Components

// #region Hooks
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
// #endregion Hooks

import "./LiveItem.css";

const MAP_MODE = { HISTORY: "history", LIVE: "live" };

const BaseLiveItem = ({ itemId, mode = COMPANY_DOMAINS.STUDENT }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  // "Bugün" hesabı company timezone'una göre; Travel.day de aynı güne göre yazılıyor.
  const companyTimezone = auth.company?.timezone || auth.timezone;
  const [currentStudents, setCurrentStudents] = useState([]);

  const {
    users,
    userMap,
    studentToParentMap,
    vehicleRoutes,
    standartPersonelServices,
    shiftPersonelServices,
    schools,
    schoolOptions,
    vehicles,
  } = useContext(StoreContext);

  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [travels, setTravels] = useState([]);
  const [travelOptions, setTravelOptions] = useState([]);
  const [travelLocations, setTravelLocations] = useState([]);
  const [travelActions, setTravelActions] = useState([]);
  const [qrAttendances, setQrAttendances] = useState([]);
  const [mapMode, setMapMode] = useState(MAP_MODE.HISTORY);

  const locationsREF = useRef([]);
  const mapREF = useRef(null);
  const mapsREF = useRef(null);
  const polylineREF = useRef(null);
  const mapModeREF = useRef(mapMode);
  const [school, setSchool] = useState();
  const [index, setIndex] = useState(0);
  const indexREF = useRef(0);
  const playerStateREF = useRef(PlayerStates.NOTSTARTED);

  const [formState, inputHandler] = useForm(
    {
      travel: { value: "", isValid: false },
    },
    true,
  );

  const { isLoading, error, sendRequest, clearError } = useHttpClient();
  const { sendRequest: sendQRRequest } = useHttpClient();
  const { isLoading: isFinishing, sendRequest: sendFinishRequest } = useHttpClient();

  const hasTelemetryPermission = Boolean(auth.permissions?.[PERMISSION_TYPES.ACCESS_TO_TELEMETRY]);
  const arventoLive = hasTelemetryPermission && Boolean(auth.company?.arventoConfig?.enabled);
  const geotabLive = hasTelemetryPermission && Boolean(auth.company?.geotabConfig?.enabled);
  const showTelemetryLive = arventoLive || geotabLive;
  // Geotab demo envanter yetkisi taşımaz. Arvento hesabı araç id'siyle okumaya devam eder.
  const useGeotabPositions = geotabLive && !arventoLive;

  const matchedVehicle = useMemo(
    () => findVehicleByPlate(vehicles, loadedItem?.plate),
    [vehicles, loadedItem?.plate],
  );

  const vehicleLive = useTelemetryVehicle({
    vehicleId: matchedVehicle?.id,
    enabled: showTelemetryLive && !useGeotabPositions && mapMode === MAP_MODE.LIVE && !!matchedVehicle?.id,
  });
  const plateLive = useTelemetryByPlate({
    plate: loadedItem?.plate,
    enabled: useGeotabPositions && mapMode === MAP_MODE.LIVE && !!loadedItem?.plate,
  });
  const liveSource = useGeotabPositions ? plateLive : vehicleLive;
  const liveSnapshot = liveSource.snapshot;
  const isLiveLoading = liveSource.isLoading;
  const liveError = liveSource.error;
  const liveLoadedAt = liveSource.lastLoadedAt;

  useEffect(() => {
    setMapMode(useGeotabPositions ? MAP_MODE.LIVE : MAP_MODE.HISTORY);
  }, [itemId, useGeotabPositions]);

  useEffect(() => {
    mapModeREF.current = mapMode;
  }, [mapMode]);

  const clearPolylines = () => {
    if (polylineREF.current) {
      polylineREF.current.setMap(null);
      polylineREF.current = null;
    }
  };

  const drawPolylines = (currentlocations) => {
    clearPolylines();
    if (mapModeREF.current !== MAP_MODE.HISTORY) {
      return;
    }
    if (mapREF.current === null || mapsREF.current === null) {
      return;
    }
    if (!currentlocations || currentlocations.length === 0) {
      return;
    }
    const routePolyline = new mapsREF.current.Polyline({
      path: currentlocations,
      strokeColor: "#FF0000",
      strokeOpacity: 1,
      strokeWeight: 2,
    });
    routePolyline.setMap(mapREF.current);
    polylineREF.current = routePolyline;

    // Fit map to include all locations
    try {
      const bounds = new mapsREF.current.LatLngBounds();
      currentlocations.forEach((loc) => bounds.extend(new mapsREF.current.LatLng(loc.lat, loc.lng)));
      mapREF.current.fitBounds(bounds);
    } catch (e) {}
  };

  const handleApiLoaded = (map, maps) => {
    mapREF.current = map;
    mapsREF.current = maps;
    if (mapModeREF.current === MAP_MODE.HISTORY) {
      const currentlocations = travelLocations?.map((item) => item?.location);
      drawPolylines(currentlocations);
    } else {
      clearPolylines();
    }
  };

  const fetchTravelLocations = useCallback(
    async (travelid) => {
      try {
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/track-vehicle/${travelid}`);
        setTravelLocations(responseData.travelLocations);
        locationsREF.current = responseData.travelLocations;
        drawPolylines(responseData.travelLocations.map((item) => item?.location));
      } catch (err) {}
    },
    [sendRequest],
  );

  const fetchTravels = useCallback(
    async (routeid) => {
      try {
        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/track-vehicle`,
          "POST",
          JSON.stringify({ routeid }),
          { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
        );
        return responseData;
      } catch (err) {}
    },
    [sendRequest, auth.token],
  );

  const fetchItem = useCallback(async () => {
    try {
      let currentVehicleRoute = null;
      setIsItemLoaded(false);
      setQrAttendances([]);

      if (mode === COMPANY_DOMAINS.STUDENT) {
        currentVehicleRoute = vehicleRoutes.find((vehicleRoute) => vehicleRoute.id === itemId);
        setLoadedItem(currentVehicleRoute);
        if (!currentVehicleRoute) {
          setIsItemLoaded(true);
          return;
        }
        setCurrentStudents(
          currentVehicleRoute.student.map((student) => ({
            ...student,
            location: isPickUp(currentVehicleRoute.type) ? student.locationgeton : student.locationgetoff,
            parent:
              studentToParentMap[student.id.toString()] && studentToParentMap[student.id.toString()]?.length > 0
                ? userMap[studentToParentMap[student.id.toString()][0]]
                : null,
          })),
        );
      } else {
        currentVehicleRoute = [...standartPersonelServices, ...shiftPersonelServices].find(
          (personelService) => personelService.id === itemId,
        );
        setLoadedItem(currentVehicleRoute);
        if (!currentVehicleRoute) {
          setIsItemLoaded(true);
          return;
        }
        setCurrentStudents(
          (currentVehicleRoute.users || []).map((user) => ({
            ...user,
            location: isPickUp(currentVehicleRoute.type)
              ? user.locationgeton || user.location
              : user.locationgetoff || user.location,
            parent: { ...user },
          })),
        );
      }

      const currentSchool = schools.find((s) => s.id.toString() === currentVehicleRoute.school.id.toString());
      setSchool(currentSchool);

      const routeid = itemId;
      const fetchedTravels = await fetchTravels(routeid);
      setTravels(fetchedTravels?.travels ?? []);

      const todayStr = getTodayInTimezone(companyTimezone);
      const travelsList = fetchedTravels?.travels ?? [];
      const reversedTravels = travelsList.slice().reverse();
      const todayTravelExists = reversedTravels.some((t) => t.day === todayStr);

      if (todayTravelExists) {
        setTravelOptions(reversedTravels.map((t) => ({ value: t.id, label: t.day })));
      } else {
        setTravelOptions([
          { value: "today", label: todayStr },
          ...reversedTravels.map((t) => ({ value: t.id, label: t.day })),
        ]);
      }
    } catch (err) {
      console.log(err);
    }
    setIsItemLoaded(true);
  }, [
    vehicleRoutes,
    schools,
    itemId,
    fetchTravels,
    studentToParentMap,
    userMap,
    standartPersonelServices,
    shiftPersonelServices,
    mode,
    companyTimezone,
  ]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const play = useCallback(() => {
    if (playerStateREF.current === PlayerStates.PLAYING) {
      if (indexREF.current === locationsREF.current.length - 1) {
        onIndexChangeHandler(0);
      } else {
        onIndexChangeHandler(indexREF.current + 1);
      }
    }
  }, []);

  useEffect(() => {
    const playerInterval = setInterval(() => {
      play();
    }, 500);
    return () => {
      clearInterval(playerInterval);
    };
  }, [play]);

  const fetchTravelActions = useCallback(
    async (travelid) => {
      if (!travelid) {
        return;
      }
      try {
        const responseData = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/dailyaction/${travelid}`);
        let currentActions = responseData.travelActions;
        currentActions = currentActions.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
        setTravelActions(currentActions);
        await fetchTravelLocations(travelid);
        setIndex(0);
      } catch (err) {}
    },
    [fetchTravelLocations, sendRequest],
  );

  const fetchQRAttendance = useCallback(
    async (day) => {
      if (!day || !itemId) {
        setQrAttendances([]);
        return;
      }
      try {
        const data = await sendQRRequest(
          `${process.env.REACT_APP_BACKEND_URL}/qr-attendance?personelServiceId=${itemId}&day=${day}`,
        );
        setQrAttendances(data.attendances || []);
      } catch (err) {
        setQrAttendances([]);
      }
    },
    [sendQRRequest, itemId],
  );

  const isTodayWithoutTravel = formState.inputs.travel.value === "today";

  const selectedTravel = useMemo(() => {
    const selectedId = formState.inputs.travel.value;
    if (!selectedId || selectedId === "today" || travels.length === 0) return null;
    return travels.find((t) => t.id === selectedId) || null;
  }, [formState.inputs.travel.value, travels]);

  const selectedTravelStatus = isTodayWithoutTravel ? TRAVEL_STATUS_TYPE.NOTSTARTED : (selectedTravel?.status ?? null);

  const canFinishService =
    selectedTravelStatus !== null &&
    selectedTravelStatus !== TRAVEL_STATUS_TYPE.NOTSTARTED &&
    selectedTravelStatus !== TRAVEL_STATUS_TYPE.FINISHED;

  const handleFinishService = useCallback(async () => {
    if (!selectedTravel) return;
    if (!window.confirm(t("confirmFinishService"))) return;

    const isPersonelService = mode === COMPANY_DOMAINS.PERSONEL;
    try {
      await sendFinishRequest(
        `${process.env.REACT_APP_BACKEND_URL}/travel/${selectedTravel.id}`,
        "PATCH",
        JSON.stringify({
          route: loadedItem.id,
          status: TRAVEL_STATUS_TYPE.FINISHED,
          isPersonelService,
        }),
        { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      );
      setTravels((prev) =>
        prev.map((tr) => (tr.id === selectedTravel.id ? { ...tr, status: TRAVEL_STATUS_TYPE.FINISHED } : tr)),
      );
      fetchTravelActions(selectedTravel.id);
      alert(t("serviceFinishedSuccess"));
    } catch (err) {}
  }, [selectedTravel, mode, loadedItem, auth.token, sendFinishRequest, fetchTravelActions, t]);

  const handleTravelSelected = useCallback(
    async (travelid) => {
      if (travelid === "today") {
        setTravelActions([]);
        setTravelLocations([]);
        locationsREF.current = [];
        setQrAttendances([]);
        return;
      }
      fetchTravelActions(travelid);
      if (mode === COMPANY_DOMAINS.PERSONEL && travelid) {
        const matchedTravel = travels.find((t) => t.id === travelid);
        if (matchedTravel?.day) {
          const day = matchedTravel.day.replace(/\//g, "-");
          fetchQRAttendance(day);
        }
      }
    },
    [fetchTravelActions, mode, travels, fetchQRAttendance],
  );

  const onIndexChangeHandler = (currentIndex) => {
    setIndex(() => currentIndex);
    indexREF.current = currentIndex;
  };

  const onPlayerStateChangeHandler = (currentState) => {
    playerStateREF.current = currentState;
  };
  const onFullScreenChangeHandler = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const getMapOptions = (maps) => ({
    streetViewControl: false,
    scaleControl: true,
    fullscreenControl: true,
    styles: [
      { stylers: [{ visibility: "on" }] },
      { featureType: "road", elementType: "labels", stylers: [{ visibility: "on" }] },
      { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
    ],
    gestureHandling: "greedy",
    disableDoubleClickZoom: false,
    minZoom: 11,
    maxZoom: 25,
    mapTypeControl: false,
    mapTypeId: maps.MapTypeId.ROADMAP,
    mapTypeControlOptions: {
      style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: maps.ControlPosition.BOTTOM_CENTER,
      mapTypeIds: [maps.MapTypeId.ROADMAP, maps.MapTypeId.SATELLITE, maps.MapTypeId.HYBRID],
    },
    zoomControl: false,
    clickableIcons: false,
  });

  // Compute map default center as the center of all travel locations
  const mapPoints = useMemo(
    () => (Array.isArray(travelLocations) ? travelLocations.map((i) => i?.location).filter(Boolean) : []),
    [travelLocations],
  );

  const defaultCenter = useMemo(() => {
    if (mapPoints.length === 0) {
      const fallbackLat = locationsREF.current?.[index]?.location?.lat ?? 39;
      const fallbackLng = locationsREF.current?.[index]?.location?.lng ?? 35;
      return { lat: fallbackLat, lng: fallbackLng };
    }
    let minLat = mapPoints[0].lat;
    let maxLat = mapPoints[0].lat;
    let minLng = mapPoints[0].lng;
    let maxLng = mapPoints[0].lng;
    for (let i = 1; i < mapPoints.length; i++) {
      const p = mapPoints[i];
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
    return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
  }, [mapPoints, index]);

  const liveMapCenter = useMemo(() => {
    if (liveSnapshot?.location?.lat != null && liveSnapshot?.location?.lng != null) {
      return { lat: liveSnapshot.location.lat, lng: liveSnapshot.location.lng };
    }
    if (school?.location?.lat != null) {
      return { lat: school.location.lat, lng: school.location.lng };
    }
    return { lat: 36.8969, lng: 30.7133 };
  }, [liveSnapshot, school]);

  useEffect(() => {
    if (mapMode === MAP_MODE.LIVE) {
      clearPolylines();
      return;
    }
    const currentlocations = travelLocations?.map((item) => item?.location);
    drawPolylines(currentlocations);
  }, [mapMode, travelLocations]);

  useEffect(() => {
    if (mapMode !== MAP_MODE.LIVE || !mapREF.current || !liveSnapshot?.location) return;
    mapREF.current.panTo({ lat: liveSnapshot.location.lat, lng: liveSnapshot.location.lng });
    mapREF.current.setZoom(15);
  }, [mapMode, liveSnapshot?.location?.lat, liveSnapshot?.location?.lng]);

  // QR Attendance: determine if the feature is enabled for this school/route
  const isQRAttendanceEnabled = useMemo(() => {
    if (mode !== COMPANY_DOMAINS.PERSONEL || !school || !loadedItem) return false;
    const setting = school.vehicleQRAttendance;
    if (!setting || setting === "NOT_USED") return false;
    const routeType = loadedItem.routeType || loadedItem.type;
    if (setting === "BOTH") return true;
    if (setting === "PICK_UP_ONLY" && routeType === "PICK_UP") return true;
    if (setting === "DROP_OFF_ONLY" && routeType === "DROP_OFF") return true;
    return false;
  }, [mode, school, loadedItem]);

  // QR Attendance: compute scanned (assigned/unassigned) and not-scanned lists
  const qrAttendanceData = useMemo(() => {
    if (!isQRAttendanceEnabled) return { scannedAssigned: [], scannedUnassigned: [], notScanned: [], total: 0 };

    const assignedUserIds = new Set(currentStudents.map((s) => s.id?.toString()));
    const scannedUserIds = new Set(qrAttendances.map((a) => a.user?.id?.toString()));

    const scannedAssigned = [];
    const scannedUnassigned = [];

    qrAttendances.forEach((a) => {
      const item = {
        id: a.user?.id || a.id,
        name: a.user?.name || a.phonenumber,
        scannedAt: a.scannedAt,
      };
      if (assignedUserIds.has(a.user?.id?.toString())) {
        scannedAssigned.push(item);
      } else {
        scannedUnassigned.push(item);
      }
    });

    const notScanned = currentStudents
      .filter((s) => !scannedUserIds.has(s.id?.toString()))
      .map((s) => ({
        id: s.id,
        name: s.name || s.parent?.name,
      }));

    return {
      scannedAssigned,
      scannedUnassigned,
      notScanned,
      total: currentStudents.length,
    };
  }, [isQRAttendanceEnabled, qrAttendances, currentStudents]);

  if (!isItemLoaded) {
    return <div className="center">{<LoadingSpinner />}</div>;
  }

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      {travels.length != 0 ? (
        <div className="vertical-flex" style={{ justifyContent: "flex-start" }}>
          <div
            className="card"
            style={{
              display: "flex",
              width: "100%",
              margin: "0 auto",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              columnGap: "20px",
              flexWrap: "wrap",

              marginTop: "0px",

              paddingTop: "0px",
              paddingBottom: "0px",
              marginBottom: "0px",
            }}
          >
            <div
              style={{
                display: "flex",
                marginTop: "10px",
                alignItems: "baseline",
                marginBottom: "0px",
                flexDirection: "row",
                columnGap: "10px",
                minWidth: "400px",
              }}
            >
              <h4 style={{ marginBottom: "0", marginTop: "0" }}>{t("selectRouteDate")}</h4>
              <CustomSelect
                options={travelOptions}
                id="travel"
                initialValue={
                  travelOptions.length > 0
                    ? travelOptions[0]
                    : travelOptions.find((option) => option.value === formState.inputs.travel.value)
                }
                onInput={inputHandler}
                initialValid={true}
                fireChange={handleTravelSelected}
              ></CustomSelect>
            </div>
            <h4 style={{ marginBottom: "0", marginTop: "0" }}>
              {schoolOptions.find((school) => school.value === loadedItem.school.id.toString())?.label ?? "-"}
            </h4>
            <span>
              <strong>{t("vehicleRoute.title")}:</strong> {loadedItem.name} (
              {isPickUp(loadedItem.type) ? t("pickUp") : t("dropOff")})
            </span>
            <span>
              <strong>{t("driver")}: </strong>
              {mode === COMPANY_DOMAINS.STUDENT
                ? loadedItem?.driver[0]?.name || "-"
                : loadedItem?.drivers[0]?.name || "-"}
            </span>
            <span>
              <strong>{t("phone")}: </strong>
              {mode === COMPANY_DOMAINS.STUDENT
                ? loadedItem.driver[0]?.phonenumber || "-"
                : loadedItem.drivers[0]?.phonenumber || "-"}
            </span>
            {loadedItem?.plate ? (
              <span>
                <strong>{t("plate")}: </strong>
                {loadedItem.plate}
              </span>
            ) : null}
            {selectedTravelStatus !== null && (
              <span
                style={{
                  padding: "4px 12px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "white",
                  backgroundColor:
                    selectedTravelStatus === TRAVEL_STATUS_TYPE.FINISHED
                      ? "#4caf50"
                      : selectedTravelStatus === TRAVEL_STATUS_TYPE.NOTSTARTED
                        ? "#9e9e9e"
                        : "#ff9800",
                }}
              >
                {t("serviceCurrentStatus")}:{" "}
                {t(TRAVEL_STATUS_TYPE_EXPLANATIONS.find((e) => e.value === selectedTravelStatus)?.label || "-")}
              </span>
            )}
            {canFinishService && (
              <Button
                type="button"
                danger
                size="small"
                onClick={handleFinishService}
                disabled={isFinishing}
                style={{ minWidth: "120px" }}
              >
                {isFinishing ? t("pleaseWait") : t("finishService")}
              </Button>
            )}
          </div>

          {isTodayWithoutTravel ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "60px 20px",
                color: "#666",
                width: "100%",
              }}
            >
              <p style={{ fontSize: "16px", fontWeight: "bold", margin: "0 0 8px 0" }}>
                {t("noTravelToday")}
              </p>
              <p style={{ fontSize: "14px", margin: 0 }}>
                {t("selectPreviousDay")}
              </p>
            </div>
          ) : travelActions?.length != 0 ? (
            <div className="live-item-right-side">
              <div className="middle-container">
                <RideSkipLivePanel
                  serviceKind={RIDE_SKIP_SERVICE_KINDS.VEHICLE_ROUTE}
                  serviceId={itemId}
                  students={currentStudents}
                />
                {isQRAttendanceEnabled && (
                  <div className="qr-attendance-container">
                    <div className="qr-attendance-header">
                      <h4>{t("qrAttendanceTitle")}</h4>
                      <div className="qr-attendance-summary">
                        {t("qrAttendanceSummary", {
                          scanned: qrAttendanceData.scannedAssigned.length,
                          total: qrAttendanceData.total,
                        })}
                        {qrAttendanceData.scannedUnassigned.length > 0 &&
                          " " +
                            t("qrAttendanceScannedUnassignedCount", {
                              count: qrAttendanceData.scannedUnassigned.length,
                            })}
                      </div>
                    </div>
                    {qrAttendanceData.scannedAssigned.length > 0 && (
                      <>
                        <div className="qr-attendance-section-label">{t("qrAttendanceScanned")}</div>
                        {qrAttendanceData.scannedAssigned.map((item) => (
                          <div key={item.id} className="qr-attendance-item qr-attendance-item--scanned">
                            <span className="qr-attendance-item-icon">
                              <CheckCircle fontSize="small" />
                            </span>
                            <span className="qr-attendance-item-name">{item.name}</span>
                            <span className="qr-attendance-item-time">
                              {item.scannedAt
                                ? new Date(item.scannedAt).toLocaleTimeString(undefined, {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hourCycle: "h23",
                                  })
                                : ""}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                    {qrAttendanceData.scannedUnassigned.length > 0 && (
                      <>
                        <div className="qr-attendance-section-label">
                          {t("qrAttendanceScannedUnassigned")}
                        </div>
                        {qrAttendanceData.scannedUnassigned.map((item) => (
                          <div
                            key={item.id}
                            className="qr-attendance-item qr-attendance-item--scanned-unassigned"
                          >
                            <span className="qr-attendance-item-icon">
                              <PersonAdd fontSize="small" />
                            </span>
                            <span className="qr-attendance-item-name">{item.name}</span>
                            <span className="qr-attendance-item-time">
                              {item.scannedAt
                                ? new Date(item.scannedAt).toLocaleTimeString(undefined, {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hourCycle: "h23",
                                  })
                                : ""}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                    {qrAttendanceData.notScanned.length > 0 && (
                      <>
                        <div className="qr-attendance-section-label">{t("qrAttendanceNotScanned")}</div>
                        {qrAttendanceData.notScanned.map((item) => (
                          <div key={item.id} className="qr-attendance-item qr-attendance-item--not-scanned">
                            <span className="qr-attendance-item-icon">
                              <RadioButtonUnchecked fontSize="small" />
                            </span>
                            <span className="qr-attendance-item-name">{item.name}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
                <div className="player-container">
                  {showTelemetryLive && loadedItem?.plate && (
                    <div className="live-item-map-mode">
                      <button
                        type="button"
                        className={`live-item-map-mode__btn ${
                          mapMode === MAP_MODE.HISTORY ? "live-item-map-mode__btn--active" : ""
                        }`}
                        onClick={() => setMapMode(MAP_MODE.HISTORY)}
                      >
                        {t("trackVehicleMapHistory")}
                      </button>
                      <button
                        type="button"
                        className={`live-item-map-mode__btn ${
                          mapMode === MAP_MODE.LIVE ? "live-item-map-mode__btn--active" : ""
                        }`}
                        onClick={() => setMapMode(MAP_MODE.LIVE)}
                      >
                        {t("trackVehicleMapLive")}
                      </button>
                    </div>
                  )}

                  <div className="live-item-map-body">
                  {mapMode === MAP_MODE.HISTORY && travelLocations.length > 0 && !isLoading && (
                    <div className="live-item-map-player">
                      <Player
                        onPlayerStateChange={onPlayerStateChangeHandler}
                        onPlayerIndexChange={onIndexChangeHandler}
                        onFullScreenChange={onFullScreenChangeHandler}
                        min={0}
                        max={travelLocations.length - 1}
                        value={index}
                        text={formatIstanbulWallClock(locationsREF.current[index]?.date, auth.timezone)}
                      />
                    </div>
                  )}

                  {mapMode === MAP_MODE.LIVE && showTelemetryLive ? (
                    <>
                      {!matchedVehicle ? (
                        <div className="center width-full live-item-map-message live-item-map-message--warn">
                          {t("trackVehiclePlateNotMatched")}
                        </div>
                      ) : isLiveLoading && !liveSnapshot ? (
                        <div className="center width-full">
                          <LoadingSpinner />
                        </div>
                      ) : liveError ? (
                        <div className="center width-full live-item-map-message live-item-map-message--error">
                          {t("telemetryLiveFetchFailed")}
                        </div>
                      ) : !liveSnapshot?.location?.lat ? (
                        <div className="center width-full live-item-map-message">
                          {t("telemetryNoLiveLocation")}
                        </div>
                      ) : (
                        <>
                          <div className="live-item-telemetry-bar">
                            <span className="live-item-telemetry-bar__status">
                              {t(`telemetryVehicleStatus_${liveSnapshot.status}`) || liveSnapshot.status}
                            </span>
                            <span>
                              {Math.round(liveSnapshot.speed || 0)} km/h · {formatTelemetryAge(liveSnapshot.ageSeconds)}
                            </span>
                            {liveLoadedAt ? (
                              <span className="live-item-telemetry-bar__sync">
                                {liveLoadedAt.toLocaleTimeString()}
                              </span>
                            ) : null}
                          </div>
                          <div className="live-item-map-canvas">
                          <GoogleMapReact
                            options={getMapOptions}
                            key={`${itemId}-live`}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
                            defaultCenter={liveMapCenter}
                            defaultZoom={14}
                          >
                            <VehicleTelemetryMarker
                              lat={liveSnapshot.location.lat}
                              lng={liveSnapshot.location.lng}
                              snapshot={liveSnapshot}
                              ageLabel={formatTelemetryAge(liveSnapshot.ageSeconds)}
                            />
                            {school && (
                              <Marker
                                Icon={SchoolIcon}
                                lat={school.location.lat}
                                lng={school.location.lng}
                                key="Okul-live"
                                info={school.name}
                                alwaysShowMarker={true}
                              />
                            )}
                            {currentStudents.map((student) => (
                              <Marker
                                Icon={RoomIcon}
                                lat={student.location.lat}
                                lng={student.location.lng}
                                key={`live-${student.id}`}
                                info={student.name}
                                alwaysShowMarker={false}
                                initialShow={false}
                              />
                            ))}
                          </GoogleMapReact>
                          </div>
                        </>
                      )}
                    </>
                  ) : !isLoading && travelLocations.length > 0 ? (
                    <div className="live-item-map-canvas">
                    <GoogleMapReact
                      options={getMapOptions}
                      key={itemId}
                      yesIWantToUseGoogleMapApiInternals
                      onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
                      defaultCenter={defaultCenter}
                      defaultZoom={13}
                    >
                      {locationsREF.current?.length && (
                        <Marker
                          Icon={DirectionsBus}
                          lat={locationsREF.current[index]?.location.lat}
                          lng={locationsREF.current[index]?.location.lng}
                          key={"asdasggh"}
                          info={
                            formatIstanbulWallClock(locationsREF.current[index]?.date, auth.timezone) +
                            " - " +
                            Math.floor(locationsREF.current[index]?.speed * 3.6) +
                            " " +
                            t("kmhour")
                          }
                          alwaysShowMarker={true}
                          initialShow={true}
                        />
                      )}
                      {school && (
                        <Marker
                          Icon={SchoolIcon}
                          lat={school.location.lat}
                          lng={school.location.lng}
                          key={"Okul"}
                          info={school.name}
                          alwaysShowMarker={true}
                        />
                      )}
                      {currentStudents.map((student) => (
                        <Marker
                          Icon={RoomIcon}
                          lat={student.location.lat}
                          lng={student.location.lng}
                          key={student.id}
                          info={student.name}
                          alwaysShowMarker={false}
                          initialShow={false}
                        />
                      ))}
                    </GoogleMapReact>
                    </div>
                  ) : (
                    !isLoading &&
                    mapMode === MAP_MODE.HISTORY && (
                      <div className="center width-full">Bu güzergah için araç konum bilgisi bulunmuyor.</div>
                    )
                  )}
                  </div>
                </div>
                <div className="actions-container">
                  {travelActions.length != 0 && (
                    <div>
                      <div
                        style={{
                          paddingBottom: "10px",
                          backgroundColor: "white",
                          position: "sticky",
                          zIndex: 1,
                          top: 0,
                        }}
                      >
                        <h4 style={{ margin: 0 }}> {t("routeMovements")}</h4>
                      </div>
                      {travelLocations && mode === COMPANY_DOMAINS.STUDENT && (
                        <div>
                          {travelActions.map((travelaction) => (
                            <div>
                              <div
                                style={{
                                  background:
                                    locationsREF.current[index]?.date.split(" ")[1] >= travelaction?.date.split(" ")[1]
                                      ? "ForestGreen"
                                      : "Gray",
                                  marginBottom: "5px",
                                  color: "white",
                                  padding: "10px",
                                  paddingLeft: "10px",
                                  borderRadius: "5px",
                                  fontSize: "14px",
                                }}
                              >
                                {formatIstanbulWallClock(travelaction?.date, auth.timezone)} {" - "}
                                {(travelaction.actiontype === TRAVEL_ACTION_TYPE.STARTED ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.FINISHED ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.QR_READED ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.QR_NEADED) &&
                                  t(
                                    TRAVEL_ACTION_TYPE_EXPLANATIONS.find((exp) => exp.value === travelaction.actiontype)
                                      ?.label ?? "-",
                                  )}
                                {(travelaction.actiontype === TRAVEL_ACTION_TYPE.APPROACH_TO_HOME ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.STUDENT_GETON_INFO) &&
                                  currentStudents.find((student) => student.id == travelaction.student)?.name}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.ARRIVED_SCHOOL &&
                                  travelaction.action != ARRIVED_SCHOOL_TYPE.BUS_ARRIVED &&
                                  currentStudents.find((student) => student.id == travelaction.student)?.name}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.ARRIVED_SCHOOL &&
                                  travelaction.action != ARRIVED_SCHOOL_TYPE.BUS_ARRIVED && <br />}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.ARRIVED_SCHOOL &&
                                  t(
                                    ARRIVED_SCHOOL_TYPE_EXPLANATIONS.find((type) => type.value === travelaction.action)
                                      .label,
                                  )}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.LEAVED_SCHOOL &&
                                  travelaction.action === LEAVED_SCHOOL_TYPE.BUS_LEAVED &&
                                  t(
                                    TRAVEL_ACTION_TYPE_EXPLANATIONS.find(
                                      (type) => type.value === TRAVEL_ACTION_TYPE.LEAVED_SCHOOL,
                                    ).label,
                                  )}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.LEAVED_SCHOOL &&
                                  travelaction.action !== LEAVED_SCHOOL_TYPE.BUS_LEAVED &&
                                  currentStudents.find((student) => student.id == travelaction.student)?.name +
                                    " " +
                                    t(
                                      TRAVEL_LEAVED_TYPE_EXPLANATIONS.find((type) => type.value === travelaction.action)
                                        .label,
                                    )}
                                {(travelaction.actiontype === TRAVEL_ACTION_TYPE.APPROACH_TO_HOME ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.STUDENT_GETON_INFO) && <br />}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.APPROACH_TO_HOME &&
                                  t(
                                    APPROACH_TO_HOME_TYPE_EXPLANATIONS.find(
                                      (type) => type.value === travelaction.action,
                                    ).label,
                                  )}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.STUDENT_GETON_INFO &&
                                  t(
                                    STUDENT_GETON_INFO_TYPE_EXPLANATIONS.find(
                                      (type) => type.value === travelaction.action,
                                    ).label,
                                  )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {travelLocations && mode === COMPANY_DOMAINS.PERSONEL && (
                        <div>
                          {travelActions.map((travelaction) => (
                            <div>
                              <div
                                style={{
                                  background:
                                    locationsREF.current[index]?.date.split(" ")[1] >= travelaction?.date.split(" ")[1]
                                      ? "ForestGreen"
                                      : "Gray",
                                  marginBottom: "5px",
                                  color: "white",
                                  padding: "10px",
                                  paddingLeft: "10px",
                                  borderRadius: "5px",
                                  fontSize: "14px",
                                }}
                              >
                                {formatIstanbulWallClock(travelaction?.date, auth.timezone)} {" - "}
                                {(travelaction.actiontype === TRAVEL_ACTION_TYPE.STARTED ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.FINISHED ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.QR_READED ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.QR_NEADED) &&
                                  t(
                                    TRAVEL_ACTION_TYPE_EXPLANATIONS.find((exp) => exp.value === travelaction.actiontype)
                                      ?.label ?? "-",
                                  )}
                                {((travelaction.actiontype === TRAVEL_ACTION_TYPE.APPROACH_TO_HOME ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.STUDENT_GETON_INFO) &&
                                  users.find((user) => user.phonenumber === travelaction.detail)?.name) ||
                                  "-"}
                                {(travelaction.actiontype === TRAVEL_ACTION_TYPE.ARRIVED_SCHOOL &&
                                  travelaction.action != ARRIVED_SCHOOL_TYPE.BUS_ARRIVED &&
                                  users.find((user) => user.phonenumber === travelaction.detail)?.name) ||
                                  "-"}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.ARRIVED_SCHOOL &&
                                  travelaction.action != ARRIVED_SCHOOL_TYPE.BUS_ARRIVED && <br />}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.ARRIVED_SCHOOL &&
                                  t(
                                    ARRIVED_SCHOOL_TYPE_EXPLANATIONS.find((type) => type.value === travelaction.action)
                                      .label,
                                  )}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.LEAVED_SCHOOL &&
                                  travelaction.action === LEAVED_SCHOOL_TYPE.BUS_LEAVED &&
                                  t(
                                    TRAVEL_ACTION_TYPE_EXPLANATIONS.find(
                                      (type) => type.value === TRAVEL_ACTION_TYPE.LEAVED_SCHOOL,
                                    ).label,
                                  )}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.LEAVED_SCHOOL &&
                                  travelaction.action !== LEAVED_SCHOOL_TYPE.BUS_LEAVED &&
                                  currentStudents.find((student) => student.id == travelaction.student)?.name +
                                    " " +
                                    t(
                                      TRAVEL_LEAVED_TYPE_EXPLANATIONS.find((type) => type.value === travelaction.action)
                                        .label,
                                    )}
                                {(travelaction.actiontype === TRAVEL_ACTION_TYPE.APPROACH_TO_HOME ||
                                  travelaction.actiontype === TRAVEL_ACTION_TYPE.STUDENT_GETON_INFO) && <br />}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.APPROACH_TO_HOME &&
                                  t(
                                    APPROACH_TO_HOME_TYPE_EXPLANATIONS.find(
                                      (type) => type.value === travelaction.action,
                                    ).label,
                                  )}
                                {travelaction.actiontype === TRAVEL_ACTION_TYPE.STUDENT_GETON_INFO &&
                                  t(
                                    STUDENT_GETON_INFO_TYPE_EXPLANATIONS.find(
                                      (type) => type.value === travelaction.action,
                                    ).label,
                                  )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="routeInfoContainer" />
          )}
        </div>
      ) : (
        <div className="center width-full">{t("noHistoryForRoute")}</div>
      )}
    </React.Fragment>
  );
};

export default BaseLiveItem;
